import React, { useEffect, useRef, useState } from "react";
import {
  startGroove,
  audioNow,
  audioSupported,
  notesFor,
  COMP_PATTERNS,
  groupStrikes,
  scoreRhythm,
} from "../../lib/keyboardPlan";
import { Icon, Keys, Piano, MidiBadge } from "../numberSenseUi";
import useMidi from "./useMidi";

/* ============================================================
   Number Sense — pattern trainer (rhythm section, structured).
   Learn one comping feel at a time: LISTEN to the app play the pattern
   over a groove, then PLAY it back and get a match score for how well
   your chord hits land on the pattern's grid. A held C chord keeps the
   focus on the rhythm first; the "through the changes" mode then runs
   the same feel over a four-chord loop — the bridge to the voicing
   track's timed comp question. Best with a MIDI keyboard.

   Props: onBack : () => void
   ============================================================ */
const CAN_PLAY = audioSupported();
const PATTERN_KEYS = Object.keys(COMP_PATTERNS);
const CHORD = notesFor("C", "I"); // ["C","E","G"] — one chord, all rhythm
const LEAD_BARS = 1; // a count-in bar to lock in before we start scoring
const MEASURE_BARS = 4; // bars graded

const PATTERN_DESC = {
  pads: "Hold the whole chord down for the bar — the simplest, most supportive comp.",
  stabs: "Short hits on the off-beats (the “&”s) — the funk / reggae push.",
  eighths: "Steady eighth-notes — driving pop and rock comping.",
  arp: "Break the chord into single notes across the bar — gentle and flowing.",
  push: "Downbeat plus the pushed “and of two” — the Charleston, pop's favorite kick.",
  half: "Two long pads a bar — half-time ballad support.",
  broken: "A soft broken-chord figure around beats 1 and 3 — the piano-ballad default.",
};

// The material under the pattern: one held chord (all rhythm), or a real
// four-chord loop so the feel has to survive the changes — the bridge to the
// timed comp question in the voicing track.
const CHANGES_LOOP = ["I", "V", "vi", "IV"];

const verdict = (s) =>
  s >= 85 ? "locked in" : s >= 60 ? "getting there" : "keep at it";

const PatternTrainerScreen = ({ onBack }) => {
  const [pattern, setPattern] = useState("pads");
  const [mode, setMode] = useState("hold"); // hold | changes
  const [phase, setPhase] = useState("idle"); // idle | listen | play | result
  const [bpm, setBpm] = useState(90);
  const [result, setResult] = useState(null);
  const [pos, setPos] = useState(-1); // playhead 16th position, -1 = off

  const degrees = mode === "changes" ? CHANGES_LOOP : ["I"];

  const grooveRef = useRef(null);
  const strikesRef = useRef([]);
  const measureRef = useRef(null);
  const endTimerRef = useRef(null);

  const stopGroove = () => {
    if (grooveRef.current) {
      grooveRef.current.stop();
      grooveRef.current = null;
    }
    if (endTimerRef.current) {
      clearTimeout(endTimerRef.current);
      endTimerRef.current = null;
    }
  };

  // Record each chord strike on the audio clock while grading.
  const strike = () => {
    if (phase === "play") strikesRef.current.push(audioNow());
  };
  const midi = useMidi(() => strike());

  // Playhead highlight while the groove runs.
  useEffect(() => {
    if (phase !== "listen" && phase !== "play") {
      setPos(-1);
      return undefined;
    }
    const id = setInterval(() => {
      const g = grooveRef.current;
      if (!g || !g.barDur) return;
      const el = audioNow() - g.startTime;
      if (el < 0) return;
      setPos(Math.floor(el / (g.barDur / 16)) % 16);
    }, 50);
    return () => clearInterval(id);
  }, [phase]);

  // Stop audio when leaving the screen.
  useEffect(() => () => stopGroove(), []);

  const startListen = () => {
    stopGroove();
    setResult(null);
    grooveRef.current = startGroove({
      key: "C",
      degrees,
      bpm,
      pattern,
      comp: true,
    });
    setPhase("listen");
  };

  const finishPlay = () => {
    stopGroove();
    const m = measureRef.current;
    if (!m) return;
    const grouped = groupStrikes(strikesRef.current);
    setResult(scoreRhythm(grouped, m.start, m.sixteenth, m.hits, MEASURE_BARS));
    setPhase("result");
  };

  const startPlay = () => {
    stopGroove();
    strikesRef.current = [];
    setResult(null);
    const g = startGroove({ key: "C", degrees, bpm, pattern, comp: false });
    grooveRef.current = g;
    const sixteenth = g.barDur / 16;
    const start = g.startTime + LEAD_BARS * g.barDur;
    measureRef.current = { start, sixteenth, hits: COMP_PATTERNS[pattern].hits };
    setPhase("play");
    const endAt = start + MEASURE_BARS * g.barDur;
    endTimerRef.current = setTimeout(
      finishPlay,
      Math.max(0, (endAt - audioNow() + 0.15) * 1000)
    );
  };

  const stop = () => {
    stopGroove();
    setPhase("idle");
  };

  const busy = phase === "listen" || phase === "play";
  const expected = new Set(COMP_PATTERNS[pattern].hits);
  const struck = new Set(result ? result.struckPos : []);

  if (!CAN_PLAY) {
    return (
      <div className="ns-root">
        <div className="ns-app">
          <div className="subbar">
            <button className="iconbtn" onClick={onBack} aria-label="back">
              <Icon name="back" />
            </button>
            <div className="subbar-title">pattern trainer</div>
          </div>
          <div className="scroll">
            <div className="ns-stats-empty">
              <Keys />
              <p>this browser can't synthesize audio, so there's nothing to play here yet.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ns-root">
      <div className="ns-app">
        <div className="subbar">
          <button className="iconbtn" onClick={onBack} aria-label="back">
            <Icon name="back" />
          </button>
          <div className="subbar-title">pattern trainer</div>
        </div>
        <div className="scroll">
          <p className="games-intro">
            Learn one comping feel at a time. <b>Listen</b> to the pattern, then{" "}
            <b>play it back</b> — you'll get a match score for how well your hits
            land. Start on a held chord, then make the feel survive a real
            four-chord loop. Best with a MIDI keyboard.
          </p>

          <div className="jam-section">feel</div>
          <div className="jam-pills">
            {PATTERN_KEYS.map((pk) => (
              <button
                key={pk}
                className={`jam-pill ${pk === pattern ? "on" : ""}`}
                disabled={busy}
                onClick={() => {
                  setPattern(pk);
                  setResult(null);
                }}
              >
                {COMP_PATTERNS[pk].label}
              </button>
            ))}
          </div>
          <p className="rt-desc">{PATTERN_DESC[pattern]}</p>

          <div className="jam-section">material</div>
          <div className="jam-pills">
            <button
              className={`jam-pill ${mode === "hold" ? "on" : ""}`}
              disabled={busy}
              onClick={() => {
                setMode("hold");
                setResult(null);
              }}
            >
              one chord
            </button>
            <button
              className={`jam-pill ${mode === "changes" ? "on" : ""}`}
              disabled={busy}
              onClick={() => {
                setMode("changes");
                setResult(null);
              }}
            >
              through the changes
            </button>
            {mode === "changes" && (
              <span className="jam-pill mono">C: {CHANGES_LOOP.join("–")}</span>
            )}
          </div>

          {/* one bar of the 16th grid: expected hits, the playhead, and (after a
              take) which you matched / missed / added */}
          <div className="rt-strip">
            {Array.from({ length: 16 }, (_, i) => {
              let cls = "rt-cell";
              if (i % 4 === 0) cls += " beat";
              if (expected.has(i)) cls += " exp";
              if (phase === "result") {
                if (expected.has(i) && struck.has(i)) cls += " got";
                else if (expected.has(i)) cls += " miss";
                else if (struck.has(i)) cls += " extra";
              }
              if (busy && i === pos) cls += " head";
              return <span className={cls} key={i} />;
            })}
          </div>

          <div className="ns-set-row">
            <div className="ns-set-label">
              <div className="ns-set-h">tempo</div>
              <div className="ns-set-sub">{bpm} bpm</div>
            </div>
            <input
              type="range"
              className="ns-range"
              aria-label="tempo (bpm)"
              min={60}
              max={130}
              step={1}
              value={bpm}
              disabled={busy}
              onChange={(e) => setBpm(Number(e.target.value))}
            />
          </div>

          {phase === "result" && result && (
            <div className="rt-result">
              <div className="rt-score" style={{ color: "var(--teal)" }}>
                {result.score}
                <span className="pct">%</span>
              </div>
              <div className="rt-verdict">{verdict(result.score)}</div>
              <div className="ns-set-sub">
                {result.matched}/{result.total} hits matched
                {result.extra ? ` · ${result.extra} extra` : ""}
              </div>
            </div>
          )}

          <div className="jam-transport">
            {busy ? (
              <button className="btn ghost" onClick={stop}>
                <Icon name="play" /> stop
              </button>
            ) : (
              <>
                <button className="btn ghost" onClick={startListen}>
                  <Icon name="play" /> listen
                </button>
                <button className="btn" onClick={startPlay}>
                  <Icon name="play" /> {phase === "result" ? "try again" : "play it"}
                </button>
              </>
            )}
          </div>
          {phase === "play" && (
            <div className="ns-set-sub" style={{ textAlign: "center" }}>
              copy the feel — {MEASURE_BARS} bars after the count-in
            </div>
          )}

          <Piano
            pressed={new Set()}
            onPress={() => strike()}
            hint={mode === "changes" ? [] : CHORD}
          />
          <MidiBadge status={midi.status} devices={midi.devices} />

          <div className="footnote">
            <Keys />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatternTrainerScreen;
